import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { z } from 'zod';
import {
  Button,
  CountrySelect,
  FormField,
  Input,
  PasswordInput,
} from '@/components';
import {
  isValidPhoneNumber,
  getCountryCallingCode,
} from 'react-phone-number-input';
import { loginUser, loginSendOtp } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';

// function GoogleIcon() {
//   return (
//     <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
//       <path
//         fill="#4285F4"
//         d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
//       />
//       <path
//         fill="#34A853"
//         d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
//       />
//       <path
//         fill="#FBBC05"
//         d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
//       />
//       <path
//         fill="#EA4335"
//         d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
//       />
//     </svg>
//   );
// }

const emailLoginSchema = z.object({
  emailOrMobile: z
    .string()
    .min(1, 'Email address is required.')
    .email('Please enter a valid email address.'),
  password: z
    .string()
    .min(1, 'Password is required.')
    .min(6, 'Password must be at least 6 characters long.'),
});

const mobileLoginSchema = z.object({
  emailOrMobile: z.string().min(1, 'Phone number is required.'),
});

export function LoginForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emailOrMobile: '',
    password: '',
  });
  const [countryCode, setCountryCode] = useState('IN');
  const [fieldErrors, setFieldErrors] = useState({});

  const isMobileLogin = /^\d/.test(formData.emailOrMobile);

  const { mutate, isPending } = useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      if (data?.tokens) {
        localStorage.setItem('access_token', data.tokens.access);
        localStorage.setItem('refresh_token', data.tokens.refresh);
        try {
          sessionStorage.removeItem('onesignal_banner_dismissed');
        } catch {
          /* ignore */
        }
      }
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.company_id) {
          localStorage.setItem('company_id', data.user.company_id);
        }
        if (data.user.company_name) {
          localStorage.setItem('company_name', data.user.company_name);
        }
      }
      if (data?.user?.onboarding_step === 'COMPLETED') {
        toast.success('Logged in successfully!');
      }
      navigate('/dashboard', { replace: true });
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          let fieldName = key;
          if (key === 'email' || key === 'phone') fieldName = 'emailOrMobile';

          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];

          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[fieldName] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Login failed. Please verify credentials.',
        );
      }
    },
  });

  const { mutate: doSendLoginOtp, isPending: isPendingSendOtp } = useMutation({
    mutationFn: loginSendOtp,
    onSuccess: (data) => {
      toast.success(data?.message || 'OTP sent successfully!');
      navigate('/verify-otp', {
        state: {
          mobile: `+${getCountryCallingCode(countryCode)}${formData.emailOrMobile}`,
          flow: 'login',
          expires_in_seconds: data?.expires_in_seconds,
        },
      });
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          let fieldName = key;
          if (key === 'email' || key === 'phone') fieldName = 'emailOrMobile';

          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];

          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[fieldName] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to send OTP. Please try again.',
        );
      }
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) setFieldErrors({ ...fieldErrors, [name]: '' });
    if (name === 'emailOrMobile') {
      if (/^\d/.test(value)) {
        const numericVal = value.replace(/\D/g, '').slice(0, 15);
        setFormData({ ...formData, [name]: numericVal });
      } else {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});

    const targetVal = formData.emailOrMobile.trim();

    if (isMobileLogin) {
      const fullNumber = `+${getCountryCallingCode(countryCode)}${targetVal}`;
      const result = mobileLoginSchema
        .extend({
          emailOrMobile: z
            .string()
            .min(1, 'Phone number is required.')
            .refine(() => isValidPhoneNumber(fullNumber), {
              message:
                'Please enter a valid phone number for the selected country.',
            }),
        })
        .safeParse(formData);

      if (!result.success) {
        const newErrors = {};
        result.error.issues.forEach((issue) => {
          const fieldName = issue.path[0];
          if (!newErrors[fieldName]) {
            newErrors[fieldName] = issue.message;
          }
        });
        setFieldErrors(newErrors);
        return;
      }

      doSendLoginOtp({ phone: fullNumber });
    } else {
      const result = emailLoginSchema.safeParse(formData);

      if (!result.success) {
        const newErrors = {};
        result.error.issues.forEach((issue) => {
          const fieldName = issue.path[0];
          if (!newErrors[fieldName]) {
            newErrors[fieldName] = issue.message;
          }
        });
        setFieldErrors(newErrors);
        return;
      }

      mutate(formData);
    }
  };

  return (
    <div className={AUTH_FORM_CARD_CLASS}>
      <form onSubmit={handleSubmit} className="w-full flex flex-col text-left">
        <FormField
          label="Email Address / Phone Number"
          htmlFor="emailOrMobile"
          error={fieldErrors.emailOrMobile}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <div className="flex gap-2 w-full">
            {isMobileLogin && (
              <CountrySelect
                value={countryCode}
                onChange={setCountryCode}
                disabled={isPending || isPendingSendOtp}
                className="w-[90px] sm:w-[100px] shrink-0 h-[52px]"
              />
            )}
            <Input
              size="lg"
              type="text"
              id="emailOrMobile"
              name="emailOrMobile"
              value={formData.emailOrMobile}
              onChange={handleChange}
              placeholder="Enter your email or phone number"
              error={!!fieldErrors.emailOrMobile}
              className={`flex-grow min-w-0 ${AUTH_INPUT_CLASS}`}
              disabled={isPending || isPendingSendOtp}
            />
          </div>
        </FormField>

        {!isMobileLogin && (
          <FormField
            label="Password"
            htmlFor="password"
            error={fieldErrors.password}
            className="mt-5"
            labelClassName={AUTH_LABEL_CLASS}
          >
            <PasswordInput
              size="lg"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              error={!!fieldErrors.password}
              disabled={isPending}
              className={AUTH_INPUT_CLASS}
            />
          </FormField>
        )}

        {/* Forgot Password Link */}
        {!isMobileLogin && (
          <div className="flex justify-end mt-2">
            <Link
              to="/forgot-password"
              className="text-body sm:text-h6 font-medium text-text-primary hover:text-text-primary transition-colors"
            >
              Forgot Password ?
            </Link>
          </div>
        )}

        {/* Submit Log In Button - Centered */}
        <div className="flex justify-center mt-6">
          <Button
            size="lg"
            type="submit"
            variant="primary"
            className={`${AUTH_BUTTON_CLASS} ${
              isMobileLogin ? 'w-[180px] h-[48px]' : 'w-[145px] h-[48px]'
            }`}
            disabled={isPending || isPendingSendOtp}
          >
            {isPending || isPendingSendOtp ? (
              <span className="flex items-center justify-center w-full">
                <svg
                  className="animate-spin h-5 w-5 text-surface-mint"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : isMobileLogin ? (
              <>
                <span>Send OTP</span>
                <span>→</span>
              </>
            ) : (
              <>
                <span>Log In</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>

        {/* Or Divider */}
        {/* <div className="relative flex py-2 items-center mt-6 mb-6">
          <div className="flex-grow border-t border-border-default" />
          <span className="flex-shrink mx-4 text-body sm:text-body-xl font-sf-pro-display text-primary">
            Or Login with
          </span>
          <div className="flex-grow border-t border-border-default" />
        </div> */}

        {/* Google Sign In */}
        {/* <div className="flex justify-center">
          <Button
            size="lg"
            type="button"
            variant="secondary"
            className="w-[145px] h-[48px]"
            onClick={() => navigate('/register')}
          >
            <span className="text-main-black">Google</span>
            <GoogleIcon />
          </Button>
        </div> */}

        {/* Register Bottom Footer Link */}
        <div className="text-center mt-4">
          <p className="text-body-lg font-normal font-sf-pro-display text-text-primary">
            Don&apos;t have an account?{' '}
            <Link
              to="/register"
              className="text-primary text-body-lg underline transition-colors"
            >
              Register
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
